import { useFormContext } from "react-hook-form";
import { P } from "../../../components/Typography";
import { useMyQuery } from "../../Authorization/AuthProvider";
import { ActionSteps } from "./ActionSteps";
import { useArea } from "./AreaStep";
import { FormStepCard } from "./FormStepCard";
import { SessionTodos } from "./SessionTodos";
import { SESSION_FIELDS } from "./constants";
import { useEffect, useMemo, useRef } from "react";
import { DEFAULT_VALUE_ROW } from "./ActionStepsStep";

const emptyArr = [];

const ActionStepsRequiredWhenPreviousCompleted = ({
  actionStepsHints,
  hintsLoading,
}) => {
  const methods = useFormContext();
  const { watch, trigger } = methods;
  const previousActionSteps = watch(SESSION_FIELDS.PREV_ACTION_STEPS);

  const noUncompletedPrevActions =
    previousActionSteps?.filter(({ checked }) => !checked)?.length === 0;
  const rules = useMemo(
    () =>
      noUncompletedPrevActions
        ? { required: true, minLength: 1 }
        : { required: false, minLength: 0 },
    [noUncompletedPrevActions],
  );

  const canTriggerValidationRef = useRef(false);
  useEffect(() => {
    if (!canTriggerValidationRef.current) {
      return;
    }
    // Trigger validation when action steps rules change
    trigger(SESSION_FIELDS.ACTION_STEPS);
  }, [trigger, rules]);

  return (
    <ActionSteps
      name={SESSION_FIELDS.ACTION_STEPS}
      rules={rules}
      hints={actionStepsHints}
      hintsLoading={hintsLoading}
      onRemove={() => (canTriggerValidationRef.current = true)}
      sx={{ my: 5 }}
    />
  );
};

export const useActionStepsAIHintsQuery = ({
  areaOfDevelopment,
  longTermGoal,
}) => {
  return useMyQuery({
    enabled: !!areaOfDevelopment && !!longTermGoal,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryKey: [
      "user-sessions",
      "generate-action-steps",
      areaOfDevelopment,
      longTermGoal,
    ],
    fetchDef: {
      method: "POST",
      url: `/api/latest/user-sessions/generate-action-steps`,
      payload: { areaOfDevelopment, longTermGoal },
    },
  });
};

// can be adjust or not adjust flow
// adjust - sessionSubmit (useUserSessionMutation)
// not adjusted - reflectionSubmit (useUserReflectionMutation)
//
export const SetActionStepsStep = ({
  step,
  stepper,
  data,
  submitDisabled,
  previousArea,
  previousGoal,
  adjust,
  previousActionSteps: previousActionStepsProp = [], // current session actionSteps value in DB
}) => {
  const previousActionSteps = previousActionStepsProp.filter(
    ({ checked }) => !checked, // display just not completed
  );
  const valueArr = data[SESSION_FIELDS.AREA_OF_DEVELOPMENT] || previousArea;
  const { areaText } = useArea({ valueArr });
  const actionStepsAIHintsQuery = useActionStepsAIHintsQuery({
    areaOfDevelopment: areaText,
    longTermGoal: data[SESSION_FIELDS.LONG_TERM_GOAL] || previousGoal,
  });
  const actionStepsHints = [].concat(actionStepsAIHintsQuery.data || []);
  const initialPrevActionSteps = (() => {
    // TODO: move init up to keep component independent from step order (but keep it lazy)
    if (adjust) {
      // cannot go back to reflection and previousActionSteps are refetched
      return previousActionSteps || emptyArr;
    } else return data[SESSION_FIELDS.PREV_ACTION_STEPS]; // after reflection where user can check previous action steps
  })();
  return (
    <FormStepCard
      {...{
        step,
        stepper,
        data: {
          ...data,
          [SESSION_FIELDS.ACTION_STEPS]: data[SESSION_FIELDS.ACTION_STEPS]
            ?.length
            ? data[SESSION_FIELDS.ACTION_STEPS] // going back and forth, preserving state
            : DEFAULT_VALUE_ROW,
          [SESSION_FIELDS.PREV_ACTION_STEPS]: initialPrevActionSteps,
        },
        submitDisabled,
      }}
    >
      <SessionTodos name={SESSION_FIELDS.PREV_ACTION_STEPS} />
      <P my={2}></P>
      <ActionStepsRequiredWhenPreviousCompleted
        actionStepsHints={actionStepsHints}
        hintsLoading={actionStepsAIHintsQuery.isPending}
      />
    </FormStepCard>
  );
};
